import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { combineAll, DcBaseComponent } from '@datachain/ui-sdk/common';
import { PanelAction } from '@datachain/ui-sdk/components/app-panel';
import { AppIcons } from '@datachain/ui-sdk/components/dc-icon';
import { Store } from '@ngrx/store';
import { DxDataGridComponent } from 'devextreme-angular';
import { CellHoverChangedEvent } from 'devextreme/ui/data_grid';
import {
	BehaviorSubject,
	firstValueFrom,
	Observable,
	of,
	takeUntil,
	tap,
	timer,
} from 'rxjs';

import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZES } from '../../../core/dx-types';
import { ActionIcons } from '../../../ui/app-actions.icons';
import { ComponentIcons } from '../../../ui/app-components.icons';
import { DcIcons } from '../../../ui/app-dc.icons';
import { DcLoaders } from '../../../ui/app-loaders';
import { ActionIconsInjectable } from '../../../ui/ui.module';
import {
	initMetadataList,
	MetadataSelector,
	navigateToMetadataSource,
	refreshMetadataList,
} from '../../store';
import { MetadataItemEntity } from './metadata-item.entity';
import { MetadataListEntity } from './metadata-list.entity';

@Component({
	selector: 'app-metadata-list',
	templateUrl: './metadata-list.component.html',
	styleUrls: ['./metadata-list.component.scss'],
})
export class MetadataListComponent extends DcBaseComponent implements OnInit {
	protected readonly DcIcons = DcIcons;
	protected readonly ComponentIcons = ComponentIcons;
	protected readonly DcLoaders = DcLoaders;
	protected readonly pageSize = DEFAULT_PAGE_SIZE;
	protected readonly allowPageSizes = DEFAULT_PAGE_SIZES;

	public ActionIcons = ActionIcons;
	public panelActions: Array<PanelAction> = [];
	public currentlyHovered = -1;
	public toggleActions = false;

	@ViewChild(DxDataGridComponent)
	private readonly grid: DxDataGridComponent | null = null;

	private readonly firstLoadSubject = new BehaviorSubject<boolean>(true);

	public vo$: Observable<{
		isFirstLoad: boolean;
		isLoading: boolean;
		list: MetadataListEntity;
	}>;

	public constructor(
		private readonly store: Store,
		private readonly metadataSelector: MetadataSelector,
		@Inject(ActionIconsInjectable) private readonly actionIcons: AppIcons
	) {
		super();
		this.cmpId = 'metadata-list';
		this.dxLocalStorageKey = 'dx.grid.metadata-list';

		this.vo$ = combineAll({
			isFirstLoad: this.firstLoadSubject,
			isLoading: this.metadataSelector.getLoading$(),
			list: this.metadataSelector.getMetadataList$(),
		});
	}

	public ngOnInit(): void {
		super.ngOnInit();
		this.store.dispatch(initMetadataList());
		of(null)
			.pipe(
				takeUntil(this.onDestroy$),
				tap(() => this.bootstrapIcons())
			)
			.subscribe();
	}

	public refresh(): void {
		this.firstLoadSubject.next(true);
		this.grid?.instance.beginCustomLoading('');
		this.store.dispatch(refreshMetadataList());
	}

	public search(searchTerm: string): void {
		if (this.grid === null) {
			return;
		}
		this.grid.instance.searchByText(searchTerm.trim());
	}

	public onCellHover(event: CellHoverChangedEvent): void {
		if (event.eventType === 'mouseover') {
			this.currentlyHovered = event.rowIndex;
		} else {
			this.currentlyHovered = -1;
		}
	}

	public async onGridContentReady(): Promise<void> {
		const firstLoad = await firstValueFrom(this.firstLoadSubject);

		if (firstLoad) {
			await firstValueFrom(
				timer(1000).pipe(
					tap(() => {
						this.firstLoadSubject.next(false);
						this.grid?.instance.endCustomLoading();
					})
				)
			);
		}
	}

	public navigateToMetadataSource(metadata: MetadataItemEntity): void {
		this.store.dispatch(
			navigateToMetadataSource({
				metadata,
			})
		);
	}

	private async bootstrapIcons(): Promise<void> {
		const actions = [ActionIcons.ColumnExpositionChooser, ActionIcons.Filter];
		const actionMap = await firstValueFrom(
			this.actionIcons.getFromRegistry(...actions)
		);
		this.panelActions = [
			{
				order: 0,
				command: (): void => {
					this.grid?.instance.showColumnChooser();
				},
				iconName: actionMap.get(ActionIcons.ColumnExpositionChooser)?.iconName,
				description: '',
				tooltip: $localize`:i18n=@@metadata.list.columnSelection:`,
				typed: '',
			},
		];
	}
}
